/**
 * The evidence for one cell, rendered INSIDE the grid, in a row that opens
 * directly beneath the cell that was clicked.
 *
 * The number and the document that produced it end up on the same screen, in
 * the same table, a few pixels apart — which is the whole point.
 *
 * It holds NO document render of its own. A number with one source behind it
 * never gets here — that click opens the viewer popup directly — so the only
 * cells that expand are the ones with a real choice to make, and what they need
 * is the question stated: the equation, and the values that went into it. Each
 * component opens its own popup.
 *
 * The remaining case is a cell with nothing openable behind it: no citation, an
 * unopenable file, or no extraction at all. That still expands, because "there
 * is no document here" is an answer the user has to be given somewhere.
 *
 * The row it lives in spans every period column, so the tile is as wide as the
 * grid — which for a twelve-month grid is far wider than anything in it is
 * readable at. Its CONTENT is therefore capped (DOP-294) and pinned to the
 * scroll viewport's left edge (`sticky left-0`, the same mechanism as the
 * group-header rows): a click on a far-right month must show its evidence at
 * the user's current scroll position, not at a left edge twelve columns away.
 * The tile still spans the table, because a row that did not would leave a gap
 * in it.
 */

import { Info, X } from "lucide-react";
import React from "react";

import type {
	KpiOverrideOutcome,
	KpiRevertOutcome,
} from "../../services/kpiService";
import type { KpiCell, KpiMetricRow } from "../../types/kpi";
import { formatPeriod } from "./formatKpiValue";
import KpiInputList from "./KpiInputList";
import KpiSourceLink from "./KpiSourceLink";
import { kpiSourceLabel } from "./kpiSourceUri";

export interface KpiCellExpansionProps {
	metric: KpiMetricRow;
	period: string;
	/** The table's grain, so the period reads as the bucket it actually is. */
	grain?: string;
	cell: KpiCell | undefined;
	/** Opens the details panel — metadata, override, "Ask about this". */
	onOpenDetails: () => void;
	onClose: () => void;
	/** Correcting a component without leaving the grid. */
	onOverride?: (
		valueId: string,
		value: number,
	) => Promise<KpiOverrideOutcome>;
	/** Undoing a component's override without leaving the grid. */
	onRevert?: (valueId: string) => Promise<KpiRevertOutcome>;
}

const KpiCellExpansion: React.FC<KpiCellExpansionProps> = ({
	metric,
	period,
	grain = "month",
	cell,
	onOpenDetails,
	onClose,
	onOverride,
	onRevert,
}) => {
	const source = cell?.source ?? null;
	const inputs = cell?.inputs ?? [];
	// Show the equation whenever this cell was COMPUTED — which is exactly the
	// cells with no stored row behind them (`value_id === null`): derived values,
	// fill-gap values, and portfolio recomputes alike.
	//
	// Gating on `derived` instead missed the rollup, so Economic Occupancy on the
	// Portfolio tab listed its components and refused to say what was done with
	// them — the one place a derived number was least checkable. The server
	// already withholds `formula_display` where the formula did not produce the
	// number (a summed or unit-weighted rollup), so trusting its presence here is
	// safe and keeps the rule in one place.
	const formula = cell && cell.value_id === null ? metric.formula_display : null;

	return (
		<div
			data-testid="cell-expansion"
			className="border-b border-gray-200 bg-gray-50 px-3 py-3"
		>
			<div className="sticky left-3 max-w-3xl">
			<div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
				<div className="min-w-0">
					<span className="text-base font-medium text-text-strong">
						{metric.label}
					</span>
					<span className="ml-2 text-sm text-text-muted">
						{formatPeriod(period, grain)}
					</span>
				</div>
				<div className="flex shrink-0 items-center gap-1">
					<button
						type="button"
						onClick={onOpenDetails}
						className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-text-base hover:bg-gray-50"
					>
						<Info className="h-3 w-3" aria-hidden="true" />
						Details
					</button>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close source"
						className="rounded p-1 text-text-muted hover:bg-gray-200"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			</div>

			{inputs.length > 0 ? (
				<>
					{formula && (
						// The rule, before the numbers it consumed. A reader checking a
						// derived figure is asking two questions — what was computed, and
						// from what — and the first one has no answer anywhere else.
						<p className="mb-2 rounded border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs text-text-base">
							{formula}
						</p>
					)}
					<KpiInputList
						inputs={inputs}
						onOverride={onOverride}
						onRevert={onRevert}
					/>
				</>
			) : source ? (
				// Reached only when the file cannot be opened here — a row with no
				// attachment identity, or a format the viewers do not render. The
				// citation still says where the number came from, and still opens the
				// file if it can be reached at all.
				<KpiSourceLink
					source={source}
					cited={cell ? { value: cell.value, unit: cell.unit } : null}
					label={kpiSourceLabel(source)}
					className="break-words text-xs text-[#008782] underline decoration-dotted underline-offset-2 hover:text-[#004C4B]"
				/>
			) : (
				<p className="text-sm text-text-muted">
					{cell
						? "No source recorded for this value."
						: "Nothing was extracted for this metric in this period."}
				</p>
			)}
			</div>
		</div>
	);
};

export default KpiCellExpansion;
